<template>
  <div>
    <section>
      <notifications ref="notifications" />
      <div class="scip-sales">
        <nuxt-link class="backlink" :to="localePath('/partnerportal')">
          <chevron-left-icon :width="20" :height="20" />
          <span>{{ $t('toDashboard') }}</span>
        </nuxt-link>
        <application-form
          v-if="state.state === 'ready'"
          :current-step-id="state.id"
          :item-components="itemComponents"
          :product="product"
          :quote-id="contractId"
          :channel="channel"
          :language="$i18n.locale"
          @submit="handleOnSubmit"
          @saved="handleOnSaved"
          @goToStep="goToStep"
          @error="onError"
        >
          <template #loadingIndicator>
            <div class="loadingOverlay">
              <dx-loading-spinner />
            </div>
          </template>

          <template #header="{ steps, current }">
            <div class="formHeader">
              <div class="progressbar">
                <progress-indicator
                  :steps-current="
                    steps.findIndex((step) => step.id === current) + 1
                  "
                  :steps-total="steps.length"
                />
              </div>
              <div class="header">
                <h2 class="font-white formHeadline">
                  {{ $t(`steps.${current}.headline`) }}
                </h2>
                <h3
                  v-if="getNextStepId(steps, current)"
                  class="font-white formSubheadline"
                >
                  {{ $t('nextStep') }}:
                  {{ $t(`steps.${getNextStepId(steps, current)}.headline`) }}
                </h3>
              </div>
              <div class="mainDescription">
                <h1>{{ $t('variantName') }}</h1>
                <h2>{{ $t('variantSubheadline') }}</h2>

                <p v-if="$t(`steps.${current}.description.${channel}`)">
                  {{ $t(`steps.${current}.description.${channel}`) }}
                </p>
              </div>
            </div>
          </template>

          <template
            #formNavigation="{
              isFirstStep,
              isLastStep,
              onPrevious,
              onSave,
            }"
          >
            <div class="scip-sales-navigation">
              <a
                v-if="!isFirstStep"
                key="previous"
                type="button"
                class="font-white font-small back-button"
                @click="onPrevious"
              >
                {{ $t('back') }}
              </a>

              <dx-button
                key="save"
                type="button"
                theme="primary"
                :text="$t(`save.${channel}`)"
                @click="onSave"
              />

              <dx-button
                v-if="channel !== 'employee'"
                key="submit"
                type="submit"
                theme="primary"
                :text="
                  $t(isLastStep ? `submit.${channel}` : `continue.${channel}`)
                "
              />
            </div>
          </template>

          <template #tariff="{ tariff, paymentInterval }">
            <tariffbox
              :tariff="tariff"
              :payment-interval="paymentInterval"
              :product-name="$t('variantName')"
            />
          </template>
        </application-form>
      </div>
    </section>
  </div>
</template>

<script>
import {
  defineComponent,
  onMounted,
  reactive,
  ref,
  getCurrentInstance,
} from '@nuxtjs/composition-api'
import {
  ApplicationForm,
  provideApplicationFormContext,
} from '@scip-sales/application-form'
import { defaultComponents } from '@scip-sales/application-form-chakra-components'

import {
  DxButton,
  Notifications,
  LoadingSpinner as DxLoadingSpinner,
} from '@sumcumo/dextra-frontend-component-lib'
import productTranslationsQueryGql from '~/domains/application/__gql__/queries/productTranslations.gql'
import Tariffbox from '~/components/DX-ScipSales/Tariffbox'
import ProgressIndicator from '~/components/DX-ScipSales/ProgressIndicator'

import DxDownloadAdapter from '~/components/scipSalesAdapters/DX-DownloadAdapter'
import DxTextAdapter from '~/components/scipSalesAdapters/DX-TextAdapter'
import DxTextAreaAdapter from '~/components/scipSalesAdapters/DX-TextAreaAdapter'
import DxNumberAdapter from '~/components/scipSalesAdapters/DX-NumberAdapter'
import DxMoneyAdapter from '~/components/scipSalesAdapters/DX-MoneyAdapter'
import DXDateInputAdapter from '~/components/scipSalesAdapters/DX-DateInputAdapter'
import DxBooleanAdapter from '~/components/scipSalesAdapters/DX-BooleanAdapter'
import DxSelectAdapter from '~/components/scipSalesAdapters/DX-SelectAdapter'
import DxLocationsFieldAdapter from '~/components/scipSalesAdapters/DX-LocationsFieldAdapter'
import DxRadioGroupAdapter from '~/components/scipSalesAdapters/DX-RadioGroupAdapter'
import DxZipCityAdapter from '~/components/scipSalesAdapters/DX-ZipCityAdapter'
import DxModuleSelectionAdapter from '~/components/scipSalesAdapters/DX-ModuleSelectionAdapter'
import DxGlobalErrorMessageAdapter from '~/components/scipSalesAdapters/DX-GlobalErrorMessageAdapter'
import DxSummaryAdapter from '~/components/scipSalesAdapters/DX-SummaryAdapter'
import DxContentItemAdapter from '~/components/scipSalesAdapters/DX-ContentItemAdapter'
import ChevronLeftIcon from '~/components/icons/ChevronLeftIcon'

import { useI18n } from '~/javascripts/composables/i18n'
import { useRouter } from '~/javascripts/composables/route'
import { useApolloProvider } from '~/javascripts/composables/apollo'

import scrollFirstErrorIntoView from '~/javascripts/helper/scollIntoView'

// eslint-disable-next-line vue/require-direct-export
export default defineComponent({
  name: 'ScipSales',
  components: {
    ApplicationForm,
    ChevronLeftIcon,
    DxButton,
    DxLoadingSpinner,
    Tariffbox,
    ProgressIndicator,
    Notifications,
  },
  props: {
    productKey: {
      type: String,
      required: true,
    },
    variantKey: {
      type: String,
      required: true,
    },
    versionNumber: {
      type: Number,
      default: 1,
    },
    contractId: {
      type: Number,
      default: 0,
    },
    authToken: {
      type: String,
      default: null,
    },
    channel: {
      type: String,
      default: 'broker',
    },
  },
  // eslint-disable-next-line max-statements, max-lines-per-function
  setup(props) {
    const { $nextTick } = getCurrentInstance().proxy
    const uri =
      process.client && window.localStorage.getItem('@scip-sales/url')
        ? window.localStorage.getItem('@scip-sales/url')
        : process.env.DEXTRA_SCIPSALES_URI
    provideApplicationFormContext({
      uri,
      getAuthToken,
    })

    const { $i18n, $t } = useI18n()
    const $router = useRouter()
    const $apolloProvider = useApolloProvider()

    const state = reactive({
      id: undefined,
      state: 'loading',
    })

    const product = reactive({
      key: props.productKey,
      variantKey: props.variantKey,
      versionNumber: props.versionNumber,
      preview: true,
    })

    const goToStep = (newId) => {
      state.id = newId
    }

    const goToNextState = (newState) => {
      switch (newState) {
        case 'success':
          state.state = 'ready'
          break
        case 'loading':
          state.state = 'ready'
          break
        case 'ready':
          state.state = 'success'
          break
        default:
          state.state = newState
      }
    }

    function getAuthToken() {
      if (props.authToken) {
        return props.authToken
      }

      try {
        const { authentication } = JSON.parse(localStorage.getItem('dextra'))
        return authentication.token.jwt
      } catch {
        return null
      }
    }

    const getNextStepId = (steps, current) => {
      const currentIndex = steps.findIndex((step) => step.id === current)
      if (steps[currentIndex + 1]) {
        return steps[currentIndex + 1].id
      }

      return null
    }

    async function fetchLocalesForProduct() {
      const { data } = await $apolloProvider.clients.cms.query({
        query: productTranslationsQueryGql,
        variables: {
          productKey: product.key,
          productVariantKey: product.variantKey,
          productVersionNumber: `${product.versionNumber}`,
          locales: [$i18n.locale],
        },
        fetchPolicy: 'no-cache',
      })

      if (data) {
        const { translations } = data.productTranslations[0]
        $i18n.mergeLocaleMessage($i18n.locale, translations)
      }
    }

    onMounted(async () => {
      await fetchLocalesForProduct()

      goToNextState(state.state)
    })

    const notifications = ref(null)
    const onError = (errors) => {
      $nextTick(() => {
        scrollFirstErrorIntoView()
      })

      /**
       * available props on error
       * source: 'submit' | 'tariff' | 'save'
       * message: string | null
       * attributeKey?: string
       * moduleKey?: string | null
       */

      // While GQL has a `required` code sent, the middleware drops this.
      // so I can just check whether there are validation errors on save.
      // I'll just assume these are missing required fields for now :shrug:
      const hasSaveErrors = errors.some(
        (error) =>
          error.message === 'ValidationError' && error.source === 'save',
      )

      const errorMessages = [$t('errors.submit.general')]

      if (hasSaveErrors) {
        errorMessages.push($t('errors.submit.required'))
      }

      notifications.value.addNotification({
        type: 'error',
        messageKey: errorMessages.join(' '),
        timer: 6000,
      })
    }

    function handleOnSubmit() {
      $router.push(this.localePath('PartnerPortal-ClosedPolicies'))
    }

    function handleOnSaved(id) {
      if (props.channel === 'employee') {
        window.parent.postMessage(
          JSON.stringify({ contractid: id, errors: 0 }),
          '*',
        )
      } else {
        $router.push(this.localePath('PartnerPortal-OpenOffers'))
      }
    }

    return {
      itemComponents: {
        ...defaultComponents,
        DownloadItem: DxDownloadAdapter,
        TextField: DxTextAdapter,
        TextAreaField: DxTextAreaAdapter,
        NumberField: DxNumberAdapter,
        MoneyField: DxMoneyAdapter,
        DateField: DXDateInputAdapter,
        BooleanField: DxBooleanAdapter,
        SelectField: DxSelectAdapter,
        RadioGroupField: DxRadioGroupAdapter,
        ZipCityField: DxZipCityAdapter,
        ModuleSelectionField: DxModuleSelectionAdapter,
        GlobalErrorMessage: DxGlobalErrorMessageAdapter,
        SummaryItem: DxSummaryAdapter,
        LocationsField: DxLocationsFieldAdapter,
        ContentItem: DxContentItemAdapter,
      },
      state,
      product,
      goToStep,
      fetchLocalesForProduct,
      goToNextState,
      getNextStepId,
      onError,
      notifications,
      getAuthToken,
      handleOnSubmit,
      handleOnSaved,
    }
  },
})
</script>

<style lang="scss" scoped>
@import 'index';
</style>
